// LA NOTACIÓ D'ESTOCS I FLUXOS: les regles i la geometria.
//
// Fora de React i **sense cap import**, com `motorBucles.js` i `buclesCausals.js`, perquè es
// pugui provar sol. Vegeu `frontend/prova-estocs.js`.
//
// ── PER QUÈ CALIA REFER-HO ───────────────────────────────────────────────────────────────────
//
// El llenç que hi havia dibuixava **el flux com una boleta que es deixa anar on sigui**, i un
// flux que no està enganxat a res no és un flux: és un cercle. A la notació de Forrester un flux
// és **una canonada amb una vàlvula** que va d'un lloc a un altre, i el que hi passa és el que
// omple i buida els estocs. Igual el núvol, que allà era una el·lipse solta i aquí és **el que
// hi ha fora de la frontera del model**, sempre a l'extrem d'una canonada.
//
// I hi havia dues coses barrejades en una: `connections`. A la disciplina hi ha **dues menes de
// fletxa i no són intercanviables**:
//
//     CANONADA (flux)      línia doble amb vàlvula     mou MATÈRIA      omple i buida estocs
//     CONNECTOR (info)     fletxa prima corbada       mou INFORMACIÓ    no mou res mai
//
// Barrejar-les vol dir que un motor no pot saber si «A→B» és «A es buida cap a B» o «A informa
// la vàlvula de B», que són coses ben diferents. Per això aquí són dos tipus separats amb regles
// separades.
//
// ── LA LLEI QUE FA LES REGLES ────────────────────────────────────────────────────────────────
//
// **Un estoc només canvia pels fluxos que hi entren i en surten.** No hi ha cap altra manera de
// moure'l, i per això cap connector d'informació no pot apuntar a un estoc: si es pogués, el
// dibuix diria una cosa que el món no fa. La resta de regles surten d'aquí.

// Les mides, en un sol lloc. La posició d'un component és el seu CENTRE —no el cantó, com abans—
// perquè tota la geometria d'ancoratge parteix del centre i fer-ho amb el cantó era sumar
// mitges amplades a cada línia.
export const MIDES = {
  estoc: { amplada: 120, alcada: 70 },
  convertidor: { radi: 26 },
  nuvol: { rx: 34, ry: 22 },
  valvula: 15,          // la mitja amplada del llacet de la vàlvula
  canonada: 4,          // separació entre les dues línies de la canonada
};

export const TIPUS = ['stock', 'flow', 'converter', 'cloud', 'connector'];

// Les dues menes de fletxa. La resta són peces que es deixen al llenç.
export const esAresta = (t) => t === 'flow' || t === 'connector';
export const esPeca = (t) => t === 'stock' || t === 'converter' || t === 'cloud';

// ── LES REGLES ───────────────────────────────────────────────────────────────────────────────
//
// Retornen `null` si es pot, o una clau de traducció si no. Una clau i no un text: qui ho crida
// és una interfície en dos idiomes.

export function potSerCanonada(origen, desti, arestes = []) {
  if (!origen || !desti) return 'sf.errNoHiEs';
  if (origen.id === desti.id) return 'sf.errMateixa';
  const valid = (c) => c.type === 'stock' || c.type === 'cloud';
  if (!valid(origen) || !valid(desti)) return 'sf.errCanonadaExtrems';
  // Un núvol cap a un altre núvol seria matèria que entra i surt del model sense passar per
  // enlloc: no diu res.
  if (origen.type === 'cloud' && desti.type === 'cloud') return 'sf.errDosNuvols';
  if (arestes.some((a) => a.type === 'flow'
      && a.properties?.from === origen.id && a.properties?.to === desti.id)) return 'sf.errRepetida';
  return null;
}

export function potSerConnector(origen, desti, arestes = []) {
  if (!origen || !desti) return 'sf.errNoHiEs';
  if (origen.id === desti.id) return 'sf.errMateixa';
  // D'un núvol no en surt informació ni n'hi entra: és el que queda FORA del model, i del que
  // hi ha fora no en sabem res per definició.
  if (origen.type === 'cloud' || desti.type === 'cloud') return 'sf.errNuvolInfo';
  if (!(origen.type === 'stock' || origen.type === 'converter' || origen.type === 'flow'))
    return 'sf.errConnectorOrigen';
  // AQUÍ ÉS ON ES VEU LA LLEI: cap informació no pot entrar en un estoc.
  if (desti.type === 'stock') return 'sf.errCapAEstoc';
  if (!(desti.type === 'flow' || desti.type === 'converter')) return 'sf.errConnectorDesti';
  if (arestes.some((a) => a.type === 'connector'
      && a.properties?.from === origen.id && a.properties?.to === desti.id)) return 'sf.errRepetida';
  return null;
}

// ── GEOMETRIA ────────────────────────────────────────────────────────────────────────────────

// On para una línia que va del centre de `peca` cap al punt (cx, cy): al perímetre, no al centre.
// Sense això les fletxes acaben amagades sota la figura i no es veu on apunten.
export function ancora(peca, cx, cy) {
  const dx = cx - peca.x, dy = cy - peca.y;
  const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d, uy = dy / d;

  if (peca.type === 'stock') {
    const { amplada, alcada } = MIDES.estoc;
    // El factor que toca la vora del rectangle abans: el més petit dels dos.
    const tx = ux === 0 ? Infinity : (amplada / 2) / Math.abs(ux);
    const ty = uy === 0 ? Infinity : (alcada / 2) / Math.abs(uy);
    const t = Math.min(tx, ty);
    return { x: peca.x + ux * t, y: peca.y + uy * t };
  }
  if (peca.type === 'cloud') {
    const { rx, ry } = MIDES.nuvol;
    const t = 1 / Math.hypot(ux / rx, uy / ry);
    return { x: peca.x + ux * t, y: peca.y + uy * t };
  }
  // Convertidor i vàlvula: cercles.
  const r = peca.type === 'converter' ? MIDES.convertidor.radi : MIDES.valvula;
  return { x: peca.x + ux * r, y: peca.y + uy * r };
}

// La posició de la vàlvula d'una canonada: al mig, tret que algú l'hagi mogut a mà.
export function puntValvula(canonada, origen, desti) {
  const p = canonada.properties || {};
  if (Number.isFinite(p.valvulaX) && Number.isFinite(p.valvulaY)) {
    return { x: p.valvulaX, y: p.valvulaY };
  }
  if (!origen || !desti) return { x: canonada.x || 0, y: canonada.y || 0 };
  return { x: (origen.x + desti.x) / 2, y: (origen.y + desti.y) / 2 };
}

// La canonada: dues línies paral·leles de l'origen al destí, tallades allà on hi ha la vàlvula
// perquè el llacet no quedi damunt de les línies.
export function geometriaCanonada(origen, desti, valvula) {
  const a = ancora(origen, valvula.x, valvula.y);
  const b = ancora(desti, valvula.x, valvula.y);
  const dx = b.x - a.x, dy = b.y - a.y;
  const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d, uy = dy / d;
  const nx = -uy, ny = ux;                       // la perpendicular
  const s = MIDES.canonada / 2;
  const forat = MIDES.valvula + 2;               // on s'obre pas la vàlvula

  const abans = { x: valvula.x - ux * forat, y: valvula.y - uy * forat };
  const despres = { x: valvula.x + ux * forat, y: valvula.y + uy * forat };

  return {
    // Els dos trams, cadascun amb les seves dues línies.
    linies: [[a, abans], [despres, b]].flatMap(([p, q]) => [
      { x1: p.x + nx * s, y1: p.y + ny * s, x2: q.x + nx * s, y2: q.y + ny * s },
      { x1: p.x - nx * s, y1: p.y - ny * s, x2: q.x - nx * s, y2: q.y - ny * s },
    ]),
    // Cap on va la matèria: la punta de fletxa es dibuixa a l'extrem del destí.
    punta: { x: b.x, y: b.y, angle: Math.atan2(uy, ux) * 180 / Math.PI },
    angle: Math.atan2(uy, ux) * 180 / Math.PI,
    origenAncora: a, destiAncora: b,
  };
}

// El connector: una corba de Bézier quadràtica, com les relacions del llenç causal. Corbada i no
// recta perquè dos connectors entre les mateixes peces no se superposin i perquè es distingeixi
// d'un cop d'ull d'una canonada, que sempre és recta.
export function geometriaConnector(origen, desti) {
  const mx = (origen.x + desti.x) / 2, my = (origen.y + desti.y) / 2;
  const dx = desti.x - origen.x, dy = desti.y - origen.y;
  const d = Math.hypot(dx, dy) || 1;
  // La perpendicular al sentit de la fletxa. Ja canvia de signe sola quan es giren l'origen i el
  // destí: afegir-hi cap factor de separació els tornaria a superposar (vegeu `corbaLligam`).
  const desviament = Math.min(60, d * 0.22);
  const c = { x: mx - (dy / d) * desviament, y: my + (dx / d) * desviament };

  const p0 = ancora(origen, c.x, c.y);
  const p1 = ancora(desti, c.x, c.y);
  const punt = (t) => ({
    x: (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * c.x + t ** 2 * p1.x,
    y: (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * c.y + t ** 2 * p1.y,
  });
  // L'angle d'arribada, per orientar la punta de fletxa.
  const abans = punt(0.94);
  return {
    d: `M ${p0.x} ${p0.y} Q ${c.x} ${c.y} ${p1.x} ${p1.y}`,
    punt, p0, p1, c,
    angle: Math.atan2(p1.y - abans.y, p1.x - abans.x) * 180 / Math.PI,
  };
}

// El núvol: el contorn de debò, no una el·lipse ratllada. Cinc arcs, que és com es dibuixa a mà.
export function camiNuvol(cx, cy) {
  const { rx, ry } = MIDES.nuvol;
  const x = cx - rx, y = cy - ry * 0.35;
  return `M ${x + rx * 0.30} ${y + ry * 0.75}
          a ${rx * 0.30} ${ry * 0.55} 0 0 1 ${rx * 0.10} ${-ry * 1.00}
          a ${rx * 0.32} ${ry * 0.62} 0 0 1 ${rx * 0.58} ${-ry * 0.25}
          a ${rx * 0.30} ${ry * 0.55} 0 0 1 ${rx * 0.62} ${ry * 0.30}
          a ${rx * 0.28} ${ry * 0.52} 0 0 1 ${rx * 0.38} ${ry * 0.95}
          Z`.replace(/\s+/g, ' ');
}

// El llacet de la vàlvula: dos triangles oposats pel vèrtex, girats amb la canonada.
export function camiValvula() {
  const v = MIDES.valvula;
  return `M ${-v} ${-v * 0.72} L 0 0 L ${-v} ${v * 0.72} Z
          M ${v} ${-v * 0.72} L 0 0 L ${v} ${v * 0.72} Z`.replace(/\s+/g, ' ');
}

// Les propietats que porta cada tipus quan neix.
export function propietatsInicials(tipus) {
  switch (tipus) {
    case 'stock': return { initialValue: 0, units: '' };
    // El flux i el convertidor comparteixen el camp `equation` a posta: tots dos són «una
    // quantitat que surt d'una expressió», i quan hi hagi motor tots dos la llegiran igual.
    case 'flow': return { equation: '', units: '', from: null, to: null };
    case 'converter': return { equation: '', units: '' };
    case 'connector': return { from: null, to: null };
    default: return {};
  }
}

const NOTACIO = {
  MIDES, TIPUS, esAresta, esPeca, potSerCanonada, potSerConnector,
  ancora, puntValvula, geometriaCanonada, geometriaConnector,
  camiNuvol, camiValvula, propietatsInicials,
};
export default NOTACIO;
